/**
 * AlertDetailDrawer — slide-in panel that hydrates the full alert record.
 *
 * Dedrone forces a full-page navigation to see an alert's details.
 * SkyWatch keeps the report context on screen and pushes the drill-down
 * to a right-side Headless UI Dialog. A single fetch on open keeps the
 * summary endpoint lean (it only embeds row summaries, never full metadata).
 */
import { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, MapPinIcon, ClockIcon, TagIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import clsx from 'clsx';
import { reportsApi, type AlertDetail, type Severity } from '../../services/reportsApi';

function severityClass(sev: Severity): string {
  switch (sev) {
    case 'CRITICAL':
      return 'bg-rose-500/10 text-rose-400 border border-rose-500/30';
    case 'WARNING':
      return 'bg-amber-500/10 text-amber-400 border border-amber-500/30';
    case 'INFO':
    default:
      return 'bg-sky-500/10 text-sky-400 border border-sky-500/30';
  }
}

function formatDuration(sec: number): string {
  if (!Number.isFinite(sec) || sec <= 0) return '—';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  if (sec < 60) return `${Math.round(sec)}s`;
  if (m < 60) return s ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function latLngText(lat: number | null, lng: number | null): string {
  if (lat == null || lng == null) return '—';
  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
}

interface Row {
  label: string;
  value: React.ReactNode;
}

function DataRow({ label, value }: Row) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-navy-800 last:border-b-0">
      <span className="text-xs text-gray-500 flex-shrink-0 pt-0.5">{label}</span>
      <span className="text-sm text-white text-right break-words min-w-0">
        {value ?? '—'}
      </span>
    </div>
  );
}

interface Props {
  alertId: string | null;
  open: boolean;
  onClose: () => void;
}

export default function AlertDetailDrawer({ alertId, open, onClose }: Props) {
  const [detail, setDetail] = useState<AlertDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !alertId) {
      setDetail(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    reportsApi
      .getAlertDetail(alertId)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message || 'Failed to load alert');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [alertId, open]);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-navy-900/80 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 flex justify-end">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-out duration-300"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in duration-200"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="w-full max-w-xl h-full bg-navy-900 border-l border-navy-700 shadow-2xl flex flex-col">
                {/* Header */}
                <div className="flex items-start justify-between p-5 border-b border-navy-700">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {detail && (
                        <span
                          className={clsx(
                            'inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold',
                            severityClass(detail.severity),
                          )}
                        >
                          {detail.severity}
                        </span>
                      )}
                      <span className="text-xs text-gray-500 font-mono">
                        {detail ? detail.type : ''}
                      </span>
                    </div>
                    <Dialog.Title className="text-base font-semibold text-white truncate">
                      {detail?.title || 'Alert detail'}
                    </Dialog.Title>
                    {detail && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {format(new Date(detail.createdAt), 'EEE, MMM d yyyy · HH:mm:ss')}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="ml-3 p-1.5 text-gray-400 hover:text-white hover:bg-navy-800 rounded-md flex-shrink-0"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-5 space-y-6">
                  {loading && (
                    <div className="space-y-3">
                      <div className="skeleton h-4 w-1/2" />
                      <div className="skeleton h-4 w-2/3" />
                      <div className="skeleton h-4 w-1/3" />
                    </div>
                  )}

                  {error && (
                    <div className="flex items-start gap-2 p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-400 text-sm">
                      <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  {detail && !loading && !error && (
                    <>
                      {/* Summary */}
                      <section>
                        <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                          <ClockIcon className="h-3.5 w-3.5" /> Summary
                        </h4>
                        <div className="rounded-lg bg-navy-800/40 border border-navy-700 p-4">
                          <p className="text-sm text-gray-300">{detail.message}</p>
                          <div className="mt-3 pt-3 border-t border-navy-700 space-y-1">
                            <DataRow label="Duration" value={formatDuration(detail.durationSec)} />
                            <DataRow
                              label="Acknowledged"
                              value={
                                detail.acknowledged
                                  ? detail.acknowledgedAt
                                    ? format(new Date(detail.acknowledgedAt), 'MMM d HH:mm')
                                    : 'Yes'
                                  : 'No'
                              }
                            />
                          </div>
                        </div>
                      </section>

                      {/* Drone identity */}
                      {detail.track && (
                        <section>
                          <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                            <TagIcon className="h-3.5 w-3.5" /> Drone Identity
                          </h4>
                          <div className="rounded-lg bg-navy-800/40 border border-navy-700 p-4 space-y-1">
                            <DataRow label="Classification" value={detail.track.classification} />
                            <DataRow
                              label="Confidence"
                              value={
                                Number.isFinite(detail.track.confidence)
                                  ? `${(detail.track.confidence * 100).toFixed(0)}%`
                                  : '—'
                              }
                            />
                            <DataRow label="Manufacturer" value={detail.track.manufacturer} />
                            <DataRow label="Model" value={detail.track.model} />
                            <DataRow
                              label="Serial"
                              value={
                                detail.track.serialNumber ? (
                                  <span className="font-mono text-xs">{detail.track.serialNumber}</span>
                                ) : null
                              }
                            />
                            <DataRow label="Registration" value={detail.track.registration} />
                            <DataRow
                              label="First seen"
                              value={format(new Date(detail.track.firstSeen), 'MMM d HH:mm:ss')}
                            />
                            <DataRow
                              label="Last seen"
                              value={format(new Date(detail.track.lastSeen), 'MMM d HH:mm:ss')}
                            />
                          </div>
                        </section>
                      )}

                      {/* Location */}
                      {detail.track && (
                        <section>
                          <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                            <MapPinIcon className="h-3.5 w-3.5" /> Location
                          </h4>
                          <div className="rounded-lg bg-navy-800/40 border border-navy-700 p-4 space-y-1">
                            <DataRow
                              label="Start"
                              value={
                                <span className="font-mono text-xs">
                                  {latLngText(detail.track.startLatitude, detail.track.startLongitude)}
                                </span>
                              }
                            />
                            <DataRow
                              label="End"
                              value={
                                <span className="font-mono text-xs">
                                  {latLngText(detail.track.endLatitude, detail.track.endLongitude)}
                                </span>
                              }
                            />
                            <DataRow
                              label="Operator"
                              value={
                                <span className="font-mono text-xs">
                                  {latLngText(detail.track.operatorLatitude, detail.track.operatorLongitude)}
                                </span>
                              }
                            />
                          </div>
                        </section>
                      )}

                      {/* Geofence */}
                      {detail.geofence && (
                        <section>
                          <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                            Geofence
                          </h4>
                          <div className="rounded-lg bg-navy-800/40 border border-navy-700 p-4 space-y-1">
                            <DataRow label="Name" value={detail.geofence.name} />
                            <DataRow label="Type" value={detail.geofence.type} />
                          </div>
                        </section>
                      )}

                      {/* Mission */}
                      {detail.mission && (
                        <section>
                          <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                            Mission
                          </h4>
                          <div className="rounded-lg bg-navy-800/40 border border-navy-700 p-4 space-y-1">
                            <DataRow label="Name" value={detail.mission.name} />
                            <DataRow label="Status" value={detail.mission.status} />
                          </div>
                        </section>
                      )}

                      {/* Raw metadata — collapsed behind a details element so
                          operators who rarely need it aren't scrolling past a
                          wall of JSON. */}
                      {detail.metadata && Object.keys(detail.metadata).length > 0 && (
                        <section>
                          <details className="group">
                            <summary className="cursor-pointer text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 hover:text-gray-300">
                              Raw metadata
                            </summary>
                            <pre className="mt-2 p-3 rounded-lg bg-navy-950 border border-navy-700 text-[11px] text-gray-400 overflow-x-auto">
                              {JSON.stringify(detail.metadata, null, 2)}
                            </pre>
                          </details>
                        </section>
                      )}
                    </>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
